import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";

const numberOf = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? "" : parsed;
};

const resolveUnits = (props) => {
  let raw = props.units;
  if (!raw && props.unit_toggle) raw = ["px", "%"];
  if (!raw || raw.length === 0) return null;
  return raw.map((unit) =>
    typeof unit === "string" ? { key: unit } : unit
  );
};

const VariantBuilderSettingRange = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const [defaultValue, setDefaultValue] = useState(props.value ?? "");

  const [pendingUnit, setPendingUnit] = useState(null);

  const [timeoutId, setTimeoutId] = useState(null);

  const commitValue = (value) => {
    clearTimeout(timeoutId);
    const timeoutIdTemp = setTimeout(function () {
      if (props.skip_update !== true) {
        builder.update([
          {
            object_type: props.object_type,
            object_id: props.object_id,
            setting_name: props.setting_name,
            value: value,
            skip_history: props.skip_history,
          },
        ]);
      }
      if (props.callback) {
        props.callback(value);
      }
    }, 100);
    setTimeoutId(timeoutIdTemp);
  };

  function updateSetting(e) {
    let parsedFloat;
    let value = e.target.value;

    if (e.target.value !== 0) {
      parsedFloat = parseFloat(e.target.value);
    } else {
      parsedFloat = e.target.value;
    }
    if (parsedFloat !== NaN) {
      if (props.min && parsedFloat < props.min) {
        value = String(props.min);
      } else if (props.max && parsedFloat > props.max) {
        value = String(props.max);
      }
      e.target.value = value;

      setDefaultValue(value);

      if (
        (!value && props.allow_empty == true) ||
        (value &&
          (!props.min || value >= props.min) &&
          (!props.max || value <= props.max))
      ) {
        if (
          props.feature_gate_type &&
          builder.displayFeatureGate(e, props.feature_gate_type) == false
        ) {
          setDefaultValue(props.value ?? "");
          e.target.value = props.value ?? "";
          return;
        }
        commitValue(value);
      }
    }
  }

  useEffect(() => {
    setDefaultValue(props.value ?? "");
  }, [props.object_id, props.last_reset, props.value]);

  const sliderClassName = `w-full h-2 my-6 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700
          [&::-webkit-slider-thumb]:w-7
          [&::-webkit-slider-thumb]:h-7
          [&::-webkit-slider-thumb]:-mt-[6px]
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:bg-blue-600
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:transition-all
          [&::-webkit-slider-thumb]:duration-150
          [&::-webkit-slider-thumb]:ease-in-out
          [&::-webkit-slider-thumb]:dark:bg-blue-600

          [&::-moz-range-thumb]:w-7
          [&::-moz-range-thumb]:h-7
          [&::-moz-range-thumb]:appearance-none
          [&::-moz-range-thumb]:bg-white
          [&::-moz-range-thumb]:border-4
          [&::-moz-range-thumb]:border-blue-600
          [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:transition-all
          [&::-moz-range-thumb]:duration-150
          [&::-moz-range-thumb]:ease-in-out

          [&::-webkit-slider-runnable-track]:w-full
          [&::-webkit-slider-runnable-track]:h-2
          [&::-webkit-slider-runnable-track]:bg-gray-200
          [&::-webkit-slider-runnable-track]:rounded-full
          [&::-webkit-slider-runnable-track]:dark:bg-gray-700

          [&::-moz-range-track]:w-full
          [&::-moz-range-track]:h-2
          [&::-moz-range-track]:bg-gray-200
          [&::-moz-range-track]:rounded-full`;

  const inputClassName =
    "w-full p-3 bg-white dark:bg-slate-950 text-xl text-black dark:text-white rounded-xl border-solid border-[1px] border-slate-200 dark:border-slate-700 focus:border-slate-200 outline-none focus:outline-none focus:ring-0";

  const spinnerlessClassName =
    "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0";

  const units = resolveUnits(props);

  if (units) {
    const maxFor = (unit) =>
      unit.max != null ? unit.max : unit.key === "%" ? 100 : props.max;

    const stored = (defaultValue ?? "").toString().trim();
    const valueUnit = units.find(
      (unit) => unit.key && unit.key !== "" && stored.endsWith(unit.key)
    );
    const activeUnit = valueUnit || pendingUnit || units[0];
    const sliderMax = maxFor(activeUnit);
    const number = numberOf(defaultValue);

    const withUnit = (num, unit) => `${num}${unit.key}`;

    const clamp = (num, unit) => {
      const max = maxFor(unit);
      if (props.min != null && num < props.min) return props.min;
      if (max != null && num > max) return max;
      return num;
    };

    const emit = (num, unit) => {
      const value = withUnit(num, unit);
      setDefaultValue(value);
      commitValue(value);
      if (unit.callback) unit.callback(value);
    };

    const updateNumber = (raw) => {
      if (raw === "" || raw === null || raw === undefined) {
        setDefaultValue("");
        if (props.allow_empty == true) commitValue("");
        return;
      }
      const parsed = parseFloat(raw);
      if (Number.isNaN(parsed)) return;
      emit(clamp(parsed, activeUnit), activeUnit);
    };

    const switchUnit = (unit) => {
      setPendingUnit(unit);
      if (number !== "") {
        emit(clamp(number, unit), unit);
      }
    };

    const isMulti = units.length > 1;
    const unitSliderClassName = sliderClassName.replace("my-6", "my-0");

    return (
      <React.Fragment>
        <div className="w-full -mt-[5px] flex items-stretch gap-2">
          <div className="flex-1 flex items-center min-w-0">
            <input
              type="range"
              setting_name={props.setting_name}
              className={unitSliderClassName}
              value={number === "" ? (props.min != null ? props.min : 0) : number}
              onChange={(e) => emit(clamp(parseFloat(e.target.value), activeUnit), activeUnit)}
              min={props.min}
              max={sliderMax}
              step={props.range_step}
            />
          </div>

          <div
            className="relative shrink-0"
            style={{ width: isMulti ? "55px" : "65px" }}
          >
            <input
              type="number"
              setting_name={props.setting_name}
              className={`${inputClassName} ${spinnerlessClassName}${
                isMulti ? "" : " !pr-9"
              }`}
              value={number}
              onChange={(e) => updateNumber(e.target.value)}
              min={props.min}
              max={sliderMax}
              step={props.range_step}
              placeholder={props.placeholder}
            />
            {!isMulti && (
              <span
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none"
                style={{ fontSize: "12px" }}
              >
                {activeUnit.label || activeUnit.key}
              </span>
            )}
          </div>

          {isMulti && (
            <div className="shrink-0 flex items-stretch p-1 bg-slate-200 dark:bg-slate-700 rounded-lg">
              {units.map((unit) => (
                <button
                  key={unit.key}
                  type="button"
                  className={`flex-1 text-base px-3 border-none bg-transparent rounded-lg cursor-pointer ${
                    activeUnit.key === unit.key
                      ? "bg-white dark:bg-slate-950 text-black dark:text-white"
                      : "text-slate-500 dark:text-slate-300"
                  }`}
                  onClick={() => switchUnit(unit)}
                  title={`Switch to ${unit.label || unit.key}`}
                >
                  {unit.label || unit.key}
                </button>
              ))}
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="w-full -mt-[5px]">
        <div
          className="col-xs-9"
          style={{
            paddingLeft: "0px",
            paddingRight: "5px",
          }}
        >
          <input
            type="range"
            setting_name={props.setting_name}
            className={sliderClassName}
            value={
              defaultValue === "" || defaultValue == null
                ? props.min != null
                  ? props.min
                  : 0
                : defaultValue
            }
            onChange={(e) => {
              updateSetting(e);
            }}
            min={props.min}
            max={props.max}
            step={props.range_step}
          />
        </div>
        <div
          className="col-xs-3"
          style={{
            paddingLeft: "0px",
            paddingRight: "0px",
          }}
        >
          <input
            type="number"
            setting_name={props.setting_name}
            className={inputClassName}
            value={defaultValue}
            onChange={(e) => {
              updateSetting(e);
            }}
            min={props.min}
            max={props.max}
            step={props.range_step}
            placeholder={props.placeholder}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default VariantBuilderSettingRange;
